import { Pagination, Table } from "rsuite";
import {
  DateCell,
  ImageCell,
  SeeTasksCell,
  TextCell,
} from "../../components/tableItem.component";
import { ApiSchemaAppointment } from "../../models/api";
import { useMemo, useState } from "react";

type TableData = ApiSchemaAppointment & {
  name?: string;
  image?: string;
  taskDNames?: string;
  conflict?: boolean;
};

type UnassignedPatientTableProps = {
  patientData: TableData[];
  isAppointmentLoading: boolean;
  isLoading: boolean;
  addToAssigned: (rowData: TableData) => void;
  removeFromAssigned: (rowData: TableData) => void;
  searchKey?: string;
  isAssignedTable: boolean;
};

const { Column, HeaderCell, Cell } = Table;

export const AUPatientTable = ({
  patientData,
  isAppointmentLoading,
  isLoading,
  addToAssigned,
  removeFromAssigned,
  searchKey,
  isAssignedTable,
}: UnassignedPatientTableProps) => {
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const handleChangeLimit = (dataKey: number) => {
    setPage(1);
    setLimit(dataKey);
  };

  const data = useMemo(() => {
    const searchFilter = patientData.filter((appointment, i) => {
      return (
        isAssignedTable ||
        appointment.name?.toLowerCase().includes(searchKey?.toLowerCase() || "")
      );
    });
    setTotal(searchFilter.length);
    return searchFilter.filter((v, i) => {
      const start = limit * (page - 1);
      const end = start + limit;
      return i >= start && i < end;
    });
  }, [limit, page, searchKey, patientData, isAssignedTable]);

  return (
    <>
      <Table
        data={data}
        autoHeight
        bordered
        loading={isAppointmentLoading}
        renderEmpty={() =>
          isAssignedTable && (
            <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
              <h3 className="fw-light">Selected column(s) will appear here</h3>
            </div>
          )
        }
        rowClassName={(rowData) =>
          isAssignedTable && !!rowData?.conflict ? "conflict-table-row" : ""
        }
      >
        <Column width={50} align="center">
          <HeaderCell className="fw-bold text-black">
            {isAssignedTable ? (
              <span title="Remove">Remove</span>
            ) : (
              <span title="Add">Add</span>
            )}
          </HeaderCell>
          <Table.Cell>
            {(rowData: TableData) => (
              <i
                className={`fa ${isAssignedTable ? "fa-minus" : "fa-plus"} ${
                  isLoading || isAppointmentLoading ? " text-muted" : " click"
                }`}
                onClick={() => {
                  if (isAssignedTable)
                    !isLoading &&
                      removeFromAssigned(rowData as unknown as TableData);
                  else
                    !isLoading &&
                      addToAssigned(rowData as unknown as TableData);
                }}
              />
            )}
          </Table.Cell>
        </Column>

        <Column width={50} align="center">
          <HeaderCell className="fw-bold text-black">
            <span title="Photo">Photo</span>
          </HeaderCell>
          <ImageCell component="Patient" dataKey="image" />
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="Name">Name</span>
          </HeaderCell>
          <TextCell component="Patient" dataKey="name" />
        </Column>

        <Column width={100}>
          <HeaderCell className="fw-bold text-black">
            <span title="Task List">Task List</span>
          </HeaderCell>
          <Cell>
            {(rowData: TableData) => (
              <div onClick={(e) => e.preventDefault()}>
                <SeeTasksCell
                  taskDetailsList={rowData?.patient?.taskDetailsList}
                />
              </div>
            )}
          </Cell>
        </Column>

        <Column>
          <HeaderCell className="fw-bold text-black">
            <span title="Task Start Date">Task Start Date</span>
          </HeaderCell>
          <DateCell component="Patient" dataKey="appointmentStartDate" />
        </Column>

        <Column>
          <HeaderCell className="fw-bold text-black">
            <span title="Task End Date">Task End Date</span>
          </HeaderCell>
          <DateCell component="Patient" dataKey="appointmentEndDate" />
        </Column>
      </Table>
      <div className="p-2">
        <Pagination
          prev
          next
          ellipsis
          boundaryLinks
          maxButtons={2}
          size="xs"
          layout={["-", "limit", "|", "pager"]}
          total={total}
          limitOptions={[10, 20]}
          limit={limit}
          activePage={page}
          onChangePage={setPage}
          onChangeLimit={handleChangeLimit}
        />
      </div>
    </>
  );
};
